import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import TextField from "../../../components/Form/TextField";
import Select from "../../../components/Form/Select";
import Switch from "../../../components/Form/Switch";
import SelectSubscriptionStripe from "../../../components/Form/SelectSubscriptionStripe";
import IconButton from "../../../components/Buttons/IconButton";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { startCreateAdvertiserUser } from "../../../actions/auth";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import {
  ADMIN_DASHBOARD_CREATE_BRAND_MODAL_TITLE,
  ADMIN_DASHBOARD_CREATE_BRAND_MODAL_SUBTITLE,
  ADMIN_DASHBOARD_CREATE_BRAND_PLAN_ACTIVE_LABEL,
  ADMIN_DASHBOARD_USER_CREATED_SUCCESS,
  ADMIN_DASHBOARD_CREATE_BRAND_TRIAL_LABEL,
  ADMIN_DASHBOARD_CREATE_BRAND_TRIAL_YES,
  ADMIN_DASHBOARD_CREATE_BRAND_TRIAL_NO,
  ADMIN_DASHBOARD_CREATE_BRAND_PLAN_RECURRENT_LABEL,
  ADMIN_DASHBOARD_CREATE_BRAND_PLAN_LABEL,
  ADMIN_DASHBOARD_CREATE_BRAND_HAS_ADDON_SPEND_FEE_LABEL,
  ADMIN_DASHBOARD_CREATE_BRAND_AD_SPEND_FEE_PERCENTAGE_LABEL,
  ADMIN_DASHBOARD_CREATE_BRAND_STRIPE_SUBSCRIPTION_ID_LABEL,
  NAME_PLACEHOLDER,
  EMAIL_PLACEHOLDER,
  PASSWORD_PLACEHOLDER,
  BUTTON_CANCEL,
  BUTTON_SAVE,
  YES,
  NO,
  ADMIN_DASHBOARD_CREATE_BRAND_PLAN_TYPE_LABEL,
} from "../../../i18n/keysTranslations";
import {
  PLAN_TYPES_OPTIONS,
  PLAN_RECURRENT_OPTIONS,
  PLAN_OPTIONS_FOR_SELECT,
  PLAN_TYPE_CREDITS,
} from "../../../utils/constants";
import { DEFAULT_AD_SPEND_FEE_PERCENTAGE } from "../../../utils/creditActionCosts";
import { REGEX_EMAIL, REGEX_PASSWORD } from "../../../utils/regex";
import {
  FIELD_REQUIRED,
  FIELD_EMAIL_VALID,
  FIELD_PASSWORD_INSECURE,
} from "../../../i18n/keysTranslations";

const ModalCreateBrand = ({ modalOpen, onCloseModal }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const userIP = useSelectorApp((state) => state?.user?.geoIP);

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onChangeShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      signUpCountry: undefined,
      password: "",
      planType: PLAN_TYPE_CREDITS,
      isPlanActive: true,
      trial: false,
      planRecurrent: "",
      plan: "",
      hasAddonSpendFee: false,
      adSpendFeePercentage: DEFAULT_AD_SPEND_FEE_PERCENTAGE,
      stripeSubscriptionID: "",
    },
  });

  const isPlanActive = watch("isPlanActive");
  const hasAddonSpendFee = watch("hasAddonSpendFee");

  useEffect(() => {
    if (!userIP) return;
    setValue("signUpCountry", userIP?.countryCode?.toLowerCase());
    // eslint-disable-next-line
  }, [userIP]);

  const buildPayload = (values) => {
    const {
      name,
      email,
      password,
      planType,
      isPlanActive,
      trial,
      planRecurrent,
      plan,
      hasAddonSpendFee,
      adSpendFeePercentage,
      stripeSubscriptionID,
    } = values;

    const base = {
      name,
      email,
      password,
      planType,
    };

    if (!isPlanActive) {
      return { ...base, trial };
    }

    const activePayload = {
      ...base,
      isPlanActive: true,
      plan,
      planRecurrent,
      hasAddonSpendFee,
    };
    if (hasAddonSpendFee) {
      activePayload.adSpendFeePercentage = Number(adSpendFeePercentage);
    }
    if (stripeSubscriptionID?.trim()) {
      activePayload.stripeSubscriptionID = stripeSubscriptionID.trim();
    }
    return activePayload;
  };

  const onSubmit = async (values) => {
    const data = buildPayload(values);

    setIsSubmitting(true);
    try {
      const response = await dispatch(startCreateAdvertiserUser(data, true));
      if (response && response.ok) {
        await SimpleAlert({
          title: t(ADMIN_DASHBOARD_USER_CREATED_SUCCESS),
          icon: "success",
        });

        reset();
        onCloseModal();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(ADMIN_DASHBOARD_CREATE_BRAND_MODAL_TITLE)}
      description={t(ADMIN_DASHBOARD_CREATE_BRAND_MODAL_SUBTITLE)}
      maxWidth="sm"
      fullWidth
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
        }}
      >
        <TextField
          fullWidth
          label={t(NAME_PLACEHOLDER)}
          name="name"
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />

        <TextField
          fullWidth
          id="email"
          label={t(EMAIL_PLACEHOLDER)}
          name="email"
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: {
              emailValid: (value) => {
                if (!REGEX_EMAIL.test(value)) {
                  return t(FIELD_EMAIL_VALID);
                }
                return true;
              },
            },
          }}
          errors={errors}
        />

        <TextField
          fullWidth
          id="password"
          name="password"
          label={t(PASSWORD_PLACEHOLDER)}
          type={showPassword ? "text" : "password"}
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={onChangeShowPassword}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) => {
              if (!REGEX_PASSWORD.test(value)) {
                return t(FIELD_PASSWORD_INSECURE);
              }
              return true;
            },
          }}
          errors={errors}
        />

        <Select
          control={control}
          name="planType"
          label={t(ADMIN_DASHBOARD_CREATE_BRAND_PLAN_TYPE_LABEL)}
          options={PLAN_TYPES_OPTIONS}
          doTranslate={false}
          fullWidth={true}
          variant="filled"
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
          disabled={true}
        />

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <FormControl component="fieldset" sx={{ pl: 1 }}>
            <FormLabel component="legend">
              {t(ADMIN_DASHBOARD_CREATE_BRAND_PLAN_ACTIVE_LABEL)}
            </FormLabel>
            <Controller
              name="isPlanActive"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  row
                  {...field}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value === "true")}
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label={t(YES)}
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label={t(NO)}
                  />
                </RadioGroup>
              )}
            />
          </FormControl>

          {!isPlanActive && (
            <Controller
              name="trial"
              control={control}
              render={({ field }) => (
                <Box>
                  <FormLabel component="legend">
                    {t(ADMIN_DASHBOARD_CREATE_BRAND_TRIAL_LABEL)}
                  </FormLabel>
                  <Switch
                    isActive={field.value}
                    onChangeIsActive={field.onChange}
                    labelActive={ADMIN_DASHBOARD_CREATE_BRAND_TRIAL_YES}
                    labelDeactivate={ADMIN_DASHBOARD_CREATE_BRAND_TRIAL_NO}
                  />
                </Box>
              )}
            />
          )}
        </Box>

        {isPlanActive && (
          <>
            <Select
              control={control}
              name="planRecurrent"
              label={t(ADMIN_DASHBOARD_CREATE_BRAND_PLAN_RECURRENT_LABEL)}
              options={PLAN_RECURRENT_OPTIONS}
              doTranslate={true}
              fullWidth={true}
              variant="filled"
              rules={{
                required: {
                  value: true,
                  message: t(FIELD_REQUIRED),
                },
              }}
              errors={errors}
            />
            <Select
              control={control}
              name="plan"
              label={t(ADMIN_DASHBOARD_CREATE_BRAND_PLAN_LABEL)}
              options={PLAN_OPTIONS_FOR_SELECT}
              doTranslate={false}
              fullWidth={true}
              variant="filled"
              rules={{
                required: {
                  value: true,
                  message: t(FIELD_REQUIRED),
                },
              }}
              errors={errors}
            />
            <FormControl component="fieldset" sx={{ pl: 1 }}>
              <FormLabel component="legend">
                {t(ADMIN_DASHBOARD_CREATE_BRAND_HAS_ADDON_SPEND_FEE_LABEL)}
              </FormLabel>
              <Controller
                name="hasAddonSpendFee"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    row
                    {...field}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value === "true")}
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label={t(YES)}
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label={t(NO)}
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
            {hasAddonSpendFee && (
              <TextField
                fullWidth
                type="number"
                inputProps={{
                  min: 1,
                  max: 100,
                  step: 1,
                }}
                label={t(
                  ADMIN_DASHBOARD_CREATE_BRAND_AD_SPEND_FEE_PERCENTAGE_LABEL
                )}
                name="adSpendFeePercentage"
                variant="filled"
                color="primary"
                InputLabelProps={{ shrink: true }}
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                  min: {
                    value: 1,
                    message: t(FIELD_REQUIRED),
                  },
                  max: {
                    value: 100,
                    message: t(FIELD_REQUIRED),
                  },
                }}
                errors={errors}
              />
            )}
            <SelectSubscriptionStripe
              fullWidth
              name="stripeSubscriptionID"
              placeholder={t(
                ADMIN_DASHBOARD_CREATE_BRAND_STRIPE_SUBSCRIPTION_ID_LABEL
              )}
              variant="filled"
              control={control}
              rules={{
                required: {
                  value: false,
                },
              }}
              errors={errors}
            />
          </>
        )}

        <ContainerModalActions
          hasPadding={false}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            onClick: onCloseModal,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_SAVE,
            loading: isSubmitting,
            onClick: () => {},
          }}
        />
      </Box>
    </ModalBasicLayout>
  );
};

export default ModalCreateBrand;
